import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { WORKSPACES } from "./workspaces";

// Runtime, user-editable theming for the WellnessCami workspace. Edited on the
// /admin/wellness/customization page and applied live by DashboardContainer.
// Persisted to localStorage for now (per-browser) — this can move to a
// backend/user-pref store later without changing the consumer API.

export interface WellnessCustomization {
  /** The whole sidebar column background. */
  siderBg: string;
  /**
   * Sidebar accent: the selected-menu-item highlight. (The collapse-trigger
   * strip is not the accent — it's derived as a darker shade of `siderBg`.)
   */
  accent: string;
  /**
   * General (workspace-agnostic): play the interface animations this revamp
   * added — the sidebar background cross-fade, the sliding menu / workspace
   * highlight pills, and the portal's card entrances. Off restores the
   * original static build everywhere (including the clinic sidebar, whose
   * workspace switcher is shared). The OS-level `prefers-reduced-motion`
   * setting still suppresses motion independently when this is on.
   */
  animations: boolean;
  /**
   * Feature flag (per-browser, no backend): is the WellnessCami portal enabled?
   * When off, the workspace switcher drops WellnessCami and any /admin/wellness
   * route bounces back to the clinic. Toggled from the admin Configs page
   * (/admin/configs_dashboard) on the clinic side — never from inside wellness
   * itself, so it can always be turned back on. Defaults off (opt-in): flip it
   * on from Configs to reveal the workspace. A backend/cross-user flag can
   * replace it later.
   */
  portalEnabled: boolean;
}

export const WELLNESS_DEFAULTS: WellnessCustomization = {
  siderBg: WORKSPACES.wellness.theme.siderBg,
  accent: WORKSPACES.wellness.theme.menu.darkItemSelectedBg,
  animations: true,
  portalEnabled: false,
};

const STORAGE_KEY = "wellnesscami:customization:v1";
// Pre-rename key (the workspace was named "Palladino Wellness" until
// 2026-08-26). Read once, migrated, then deleted — see
// `migrateLegacyCustomization`. Do NOT rename this string: it is the literal
// key still sitting in every existing browser's localStorage.
const LEGACY_STORAGE_KEY = "palladinowellness:customization:v1";

// Only these carry over from the legacy key. The colour prefs (`siderBg`,
// `accent`) are deliberately DROPPED so every browser lands on the new plum /
// accent-blue defaults instead of resurrecting the retired lime theme. The
// three sidebar-clock/email-display toggles that lived alongside these were
// retired with the "Connected as" block (2026-09-23) — no longer migrated.
const MIGRATED_KEYS = ["portalEnabled"] as const;

/**
 * One-shot rename migration for the localStorage key. Returns the carried-over
 * prefs (or null when there's nothing to migrate) and runs BEFORE the
 * defaults-merge, so a browser that had the portal enabled under the old key
 * keeps it enabled — a silent `portalEnabled` reset would take the whole
 * workspace off the switcher with no obvious way back.
 */
function migrateLegacyCustomization(): Partial<WellnessCustomization> | null {
  const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!legacyRaw) return null;

  let carried: Partial<WellnessCustomization> = {};
  try {
    const parsed = JSON.parse(legacyRaw) as Partial<WellnessCustomization>;
    for (const key of MIGRATED_KEYS) {
      const value = parsed[key];
      if (typeof value === "boolean") carried[key] = value;
    }
  } catch {
    // Unparseable legacy blob: nothing to carry, but still retire the key.
    carried = {};
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...WELLNESS_DEFAULTS, ...carried }));
  window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  return carried;
}

function loadCustomization(): WellnessCustomization {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // No new-key value yet — carry the old key's boolean prefs over if it's
      // still around, then fall back to defaults for everything else.
      const migrated = migrateLegacyCustomization();
      return migrated ? { ...WELLNESS_DEFAULTS, ...migrated } : WELLNESS_DEFAULTS;
    }
    const parsed = JSON.parse(raw) as Partial<WellnessCustomization>;
    // Merge over defaults so a missing/renamed key falls back cleanly — and
    // filter to keys WELLNESS_DEFAULTS still declares, so a retired setting
    // (e.g. the old sidebar-clock/email-display toggles) already sitting in
    // a browser's localStorage doesn't leak back into state.
    const knownKeys = Object.keys(WELLNESS_DEFAULTS) as Array<
      keyof WellnessCustomization
    >;
    const filtered: Partial<WellnessCustomization> = {};
    for (const key of knownKeys) {
      if (key in parsed) {
        (filtered as Record<string, unknown>)[key] = (
          parsed as Record<string, unknown>
        )[key];
      }
    }
    return { ...WELLNESS_DEFAULTS, ...filtered };
  } catch {
    return WELLNESS_DEFAULTS;
  }
}

function saveCustomization(value: WellnessCustomization): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode failures — the in-memory value still applies.
  }
}

interface WellnessCustomizationContextValue {
  customization: WellnessCustomization;
  update: (patch: Partial<WellnessCustomization>) => void;
  reset: () => void;
}

const WellnessCustomizationContext =
  createContext<WellnessCustomizationContextValue | null>(null);

export const WellnessCustomizationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [customization, setCustomization] =
    useState<WellnessCustomization>(loadCustomization);

  const update = useCallback((patch: Partial<WellnessCustomization>) => {
    setCustomization((prev) => {
      const next = { ...prev, ...patch };
      saveCustomization(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setCustomization(WELLNESS_DEFAULTS);
    saveCustomization(WELLNESS_DEFAULTS);
  }, []);

  const value = useMemo(
    () => ({ customization, update, reset }),
    [customization, update, reset],
  );

  return (
    <WellnessCustomizationContext.Provider value={value}>
      {children}
    </WellnessCustomizationContext.Provider>
  );
};

const FALLBACK: WellnessCustomizationContextValue = {
  customization: WELLNESS_DEFAULTS,
  update: () => {},
  reset: () => {},
};

export function useWellnessCustomization(): WellnessCustomizationContextValue {
  return useContext(WellnessCustomizationContext) ?? FALLBACK;
}
